import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useGT } from 'gt-react';
import { X, Trash, HeadCircuit } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useAgentPanel } from '@/context/agent-panel.context';
import { useAgentState } from '@/context/agent-state.context';
import { useAgentContainer } from '@/context/agent-container.context';
import { ChatMessageList } from '@/components/common/chat/ChatMessageList';
import { ChatInput } from '@/components/common/chat/ChatInput';
import { AnnotationToolbar } from '@/components/common/chat/AnnotationToolbar';
import { useCaptureScreenHandler } from '@/hooks/useCaptureScreenHandler';
import { usePreviewCustomViewHandler } from '@/hooks/usePreviewCustomViewHandler';
import {
  AgentOverlay,
  clearOverlay,
} from '@/components/common/chat/AgentOverlay';
import { useAnnotationCanvas } from '@/context/annotation-canvas.context';
import { captureScreen } from '@/utils/capture-screen';
import type { ChatInputAttachment } from '@/components/common/chat/ChatInput';
import { useUploadEntityDocument } from '@/api/rpc/hooks/core/useEntityDocuments';
import { DocumentAssociationType } from '@erp/shared-types';
import type { MessageAttachment } from '@erp/rpc';
import {
  TodoListPanel,
  useTodoList,
  useTodoListTools,
} from '@/components/common/chat/todo-list';
// AR Vision Demo (GEN-2247) — scripted interceptor. Delete with src/demo/ar.
import { useArDemoInterceptor } from '@/demo/ar/interceptor';
// AP Invoices Demo (delete me) — scripted interceptor. Delete with src/demo/ap.
import { useApDemoInterceptor } from '@/demo/ap/agent/interceptor';
// Procurement Demo (delete me) — scripted interceptor + Nami availability on the
// procurement pages. Delete with src/demo/procurement.
import { useProcurementDemoInterceptor } from '@/demo/procurement/agent/interceptor';
import { ProcurementNamiRegistrar } from '@/demo/procurement/agent/ProcurementNamiRegistrar';

/**
 * Global chat agent renderer. Mounted once in the authenticated layout.
 * Portals chat UI into whatever AgentContainer is currently registered.
 */
export function ChatAgentRenderer() {
  return (
    <>
      {/* Procurement Demo (delete me) — registers Nami on the procurement demo
          pages. Rendered here, not inside the panel below, because the panel
          renders nothing until a module is registered. */}
      <ProcurementNamiRegistrar />
      <ChatAgentPanel />
    </>
  );
}

function ChatAgentPanel() {
  const gt = useGT();
  const { containerElement, moduleConfig, moduleConfigRef } =
    useAgentContainer();
  const { isOpen, close } = useAgentPanel();
  const {
    isLoading,
    isCapturingScreen,
    messages,
    sendMessage,
    streamingText,
    activeToolCalls,
    cancel,
    reset,
    error,
    currentThreadId,
    currentModuleKey,
    activeYields,
  } = useAgentState();
  const { clearTodoList } = useTodoList();
  const todoListTools = useTodoListTools();
  // AR Vision Demo (GEN-2247) — scripted interceptor. Delete with src/demo/ar.
  const { tryIntercept: tryArDemoIntercept } = useArDemoInterceptor();
  // AP Invoices Demo (delete me) — scripted interceptor. Delete with src/demo/ap.
  const { tryIntercept: tryApDemoIntercept } = useApDemoInterceptor();
  // Procurement Demo (delete me) — scripted interceptor. Delete with
  // src/demo/procurement.
  const {
    tryIntercept: tryProcurementDemoIntercept,
    ownsPage: procurementDemoOwnsPage,
  } = useProcurementDemoInterceptor();
  const [inputValue, setInputValue] = useState('');
  const [annotationCount, setAnnotationCount] = useState(0);
  const { isActive: isAnnotating, setActive, clear } = useAnnotationCanvas();
  const [pendingAttachments, setPendingAttachments] = useState<
    Array<
      ChatInputAttachment & {
        mimeType: string | null;
        fileSizeBytes: number | null;
      }
    >
  >([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadEntityDocument();

  const prevModuleKeyRef = useRef(currentModuleKey);
  useEffect(() => {
    if (
      prevModuleKeyRef.current !== null &&
      prevModuleKeyRef.current !== currentModuleKey
    ) {
      clearTodoList();
    }
    prevModuleKeyRef.current = currentModuleKey;
  }, [currentModuleKey, clearTodoList]);

  useCaptureScreenHandler();
  usePreviewCustomViewHandler();

  const handleInteractiveModeToggle = () => {
    if (isAnnotating) {
      setActive(false);
      clear();
    } else {
      setActive(true);
    }
  };

  const handleAttachFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files?.length) return;

      for (const file of Array.from(files)) {
        const tempId = crypto.randomUUID();
        const attachment: ChatInputAttachment & {
          mimeType: string | null;
          fileSizeBytes: number | null;
        } = {
          documentId: tempId,
          fileName: file.name,
          uploading: true,
          mimeType: file.type || null,
          fileSizeBytes: file.size,
        };
        setPendingAttachments((prev) => [...prev, attachment]);

        const entityId = currentThreadId ?? crypto.randomUUID();
        try {
          const result = await uploadMutation.mutateAsync({
            entityType: 'ai_thread_message',
            entityId,
            file,
            associationType: DocumentAssociationType.CHAT_ATTACHMENT,
          });
          setPendingAttachments((prev) =>
            prev.map((att) =>
              att.documentId === tempId
                ? {
                    ...att,
                    documentId: result.documentId,
                    uploading: false,
                    mimeType: result.mimeType,
                    fileSizeBytes: result.fileSizeBytes,
                  }
                : att,
            ),
          );
        } catch {
          setPendingAttachments((prev) =>
            prev.filter((att) => att.documentId !== tempId),
          );
        }
      }

      // Reset file input so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [currentThreadId, uploadMutation],
  );

  const handleRemoveAttachment = useCallback((documentId: string) => {
    setPendingAttachments((prev) =>
      prev.filter((att) => att.documentId !== documentId),
    );
  }, []);

  const agentOverlay = createPortal(
    <AgentOverlay onCountChange={setAnnotationCount} />,
    document.body,
  );

  const handleSend = (overrideText?: string) => {
    const trimmed = (overrideText ?? inputValue).trim();
    if ((!trimmed && !isAnnotating) || isLoading) return;
    const cfg = moduleConfigRef.current;
    if (!cfg) return;

    // AR Vision Demo (GEN-2247) — if a scripted beat matches, run it and skip
    // the real RPC send. No-op when demo mode is off. Delete with src/demo/ar.
    // AP Invoices Demo (delete me) — same, for the AP demo. Delete with
    // src/demo/ap.
    // Procurement Demo (delete me) — same, for the procurement demo, and it goes
    // first on its own pages so the AR and AP scripts cannot answer a
    // procurement question there. Delete with src/demo/procurement.
    const tryDemoIntercepts = (message: string): boolean =>
      procurementDemoOwnsPage
        ? tryProcurementDemoIntercept(message) ||
          tryArDemoIntercept(message) ||
          tryApDemoIntercept(message)
        : tryArDemoIntercept(message) ||
          tryApDemoIntercept(message) ||
          tryProcurementDemoIntercept(message);
    if (trimmed && tryDemoIntercepts(trimmed)) {
      setInputValue('');
      setPendingAttachments([]);
      if (isAnnotating) setActive(false);
      return;
    }
    const wasAnnotating = isAnnotating;
    const moduleContext = {
      ...cfg.getModuleContext?.(),
      interactiveModeActive: wasAnnotating,
    };

    const readyAttachments = pendingAttachments.filter((att) => !att.uploading);
    const attachments: MessageAttachment[] | undefined =
      readyAttachments.length > 0
        ? readyAttachments.map((att) => ({
            documentId: att.documentId,
            fileName: att.fileName,
            mimeType: att.mimeType,
            fileSizeBytes: att.fileSizeBytes,
          }))
        : undefined;

    setInputValue('');
    setPendingAttachments([]);
    if (wasAnnotating) {
      setActive(false);
    }

    const allTools = [...(cfg.tools ?? []), ...todoListTools];

    if (wasAnnotating) {
      const capturePromise = captureScreen().then((captured) => {
        clear();
        return { imageData: captured.imageData, mimeType: captured.mimeType };
      });
      void sendMessage(
        trimmed,
        cfg.moduleKey,
        cfg.currentPage,
        moduleContext,
        cfg.pageKey,
        allTools,
        capturePromise,
        attachments,
      );
    } else {
      void sendMessage(
        trimmed,
        cfg.moduleKey,
        cfg.currentPage,
        moduleContext,
        cfg.pageKey,
        allTools,
        undefined,
        attachments,
      );
    }
  };

  const stickyYieldData = useMemo(() => {
    if (activeYields.length === 0 || !moduleConfig?.renderToolResult)
      return null;
    const yield_ = activeYields[0];
    if (!yield_) return null;
    for (const msg of messages) {
      if (msg.role !== 'assistant' || !msg.toolResults) continue;
      const tr = msg.toolResults.find(
        (t) => t.toolCallId === yield_.toolCallId,
      );
      if (tr) return { msg, tr };
    }
    return null;
  }, [activeYields, messages, moduleConfig?.renderToolResult]);

  const excludeToolResultIds = useMemo(() => {
    if (!stickyYieldData) return undefined;
    return new Set([stickyYieldData.tr.toolCallId]);
  }, [stickyYieldData]);

  if (!containerElement || !moduleConfig) return null;
  if (!isOpen) return null;

  const headerIcon = moduleConfig.headerIcon ?? (
    <HeadCircuit size={18} weight="duotone" className="text-muted-foreground" />
  );
  const headerTitle = moduleConfig.headerTitle ?? 'AI Assistant';
  const placeholder = moduleConfig.placeholder;

  return (
    <>
      {agentOverlay}
      {createPortal(
        <>
          <div className="flex items-center justify-between px-3 py-2 border-b flex-shrink-0">
            <div className="flex items-center gap-2">
              {headerIcon}
              <span className="text-sm font-medium">{headerTitle}</span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  reset();
                  clearTodoList();
                }}
                disabled={isLoading || messages.length === 0}
                className="h-7 w-7 p-0"
                title={gt('Clear conversation', {
                  context: 'tooltip for button that clears chat history',
                })}
              >
                <Trash className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={close}
                className="h-7 w-7 p-0"
                title={gt('Close', {
                  context:
                    'tooltip for button that closes the chat agent panel',
                })}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {annotationCount > 0 && (
            <button
              type="button"
              onClick={clearOverlay}
              className="flex items-center justify-between w-full px-3 py-1.5 bg-muted border-b text-xs text-foreground hover:bg-accent transition-colors"
            >
              <span>
                {gt('Annotations visible on screen', {
                  context:
                    'chat agent banner when screen annotations overlay is visible',
                })}
              </span>
              <span className="font-medium">
                {gt('Dismiss', {
                  context:
                    'chat agent banner control to dismiss the annotations overlay',
                })}
              </span>
            </button>
          )}

          <TodoListPanel />

          <ChatMessageList
            messages={messages}
            streamingText={streamingText}
            isLoading={isLoading}
            isCapturingScreen={isCapturingScreen}
            error={error}
            activeToolCalls={activeToolCalls}
            messageMaxWidth="max-w-[85%]"
            renderToolResult={moduleConfig.renderToolResult}
            excludeToolResultIds={excludeToolResultIds}
            onFocusNodes={moduleConfig.onFocusNodes}
            suggestions={moduleConfig.suggestions}
            onSuggestionClick={handleSend}
          />

          {stickyYieldData && moduleConfig.renderToolResult && (
            <div className="px-4 py-2 border-t bg-background flex-shrink-0">
              {moduleConfig.renderToolResult(
                stickyYieldData.msg,
                stickyYieldData.tr,
              )}
            </div>
          )}

          <div className="p-3 border-t flex-shrink-0">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelected}
            />
            <ChatInput
              value={inputValue}
              onChange={setInputValue}
              onSend={handleSend}
              isLoading={isLoading}
              onCancel={cancel}
              placeholder={placeholder}
              autoFocus={isOpen}
              enableVoiceInput
              onVoiceTranscription={(text) => setInputValue(text ?? '')}
              onShareToggle={handleInteractiveModeToggle}
              isShareActive={isAnnotating}
              annotationToolbar={<AnnotationToolbar />}
              attachments={pendingAttachments}
              onAttachFile={handleAttachFile}
              onRemoveAttachment={handleRemoveAttachment}
            />
          </div>
        </>,
        containerElement,
      )}
    </>
  );
}
